import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  Autocomplete,
  DatePicker,
  Grid,
  FormControl,
  TextField,
  Button,
  CurrencyInput,
  TextFielDecimalNumbers,
} from "../../../../components";
import { value, InputErros, PermisoesHelper } from "../../../../helpers";
import {
  formatDate,
  responseErros,
  unformatPrice,
  voltar,
} from "../../../../utils";
import pagamentoValidator from "../middlewares/pagamento.validator";
import { ContasPagarService, notification } from "../../../../services";
import { RetirarBaixaDialog } from "../components";
import { useImportContext } from "../../../../contexts/import.context";
import {
  calcularValorAPagar,
  converterPorcentagemParaValor,
  converterValorParaPorcetagem,
  filtrarPagamentosEfetivos,
  getAtrasoDias,
  totalDespesas,
} from "../add-conta-pagar";
import { Pagamento } from "../entities";

const DadosPagamento = ({
  id,
  contaPagar,
  pagamento,
  setPagamento,
  recebedorList,
  contaList,
  meioPagamentoList,
  dadosPagamentoList,
  setDadosPagamentoList,
  setLoading,
  validarStatusCaixa,
  handlePrint,
  setReciboData,
  cadastrarCaixa,
  colaborador,
  configuracoes,
}) => {
  const { initialCollapses } = useImportContext();
  const [openRetirarBaixaDialog, setOpenRetirarBaixaDialog] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const contasPagarService = new ContasPagarService();
  const permissoesHelper = new PermisoesHelper();

  useEffect(() => {
    let pagamentoTemp = { ...pagamento };
    if (contaPagar.status === "BAIXADO") {
      const {
        totalDescontos: desconto,
        totalJuros: juros,
        totalMulta: multa,
        total: valor,
      } = totalDespesas(dadosPagamentoList);
      pagamentoTemp = { ...pagamentoTemp, desconto, juros, multa, valor };
    } else {
      pagamentoTemp.valor = contaPagar?.saldoPagar;
    }
    setPagamento(pagamentoTemp);
  }, [dadosPagamentoList]);

  const handleValorPagamentoChange = (event) => {
    const pagamentoTemp = {
      ...pagamento,
      [event.target.name]: unformatPrice(event.target.value),
    };
    setPagamento(pagamentoTemp);
  };

  const handleDateChange = (date, fieldName) => {
    if (!date) {
      date = "Invalid Date";
    }
    const dataTemp = date != "Invalid Date" ? formatDate.toSend(date) : null;
    if (fieldName == "dataPagamento" && dataTemp && pagamento.bancoCaixaId) {
      validarStatusCaixa(formatDate.toSend(date), pagamento.bancoCaixaId);
    }
    setPagamento((prevState) => ({
      ...prevState,
      [fieldName]: dataTemp,
    }));
  };

  const onChangeAutocomplete = (name, value) => {
    const valueTemp = value ? value.id : undefined;
    setPagamento((prevState) => ({
      ...prevState,
      [name]: valueTemp,
    }));
  };

  const handleInputValorPorcentagem = (event) => {
    const propriedade = event.target.name;
    const valor = Number(unformatPrice(event.target.value));
    if (propriedade === "desconto" && valor > contaPagar?.saldoPagar) {
      return notification.alertaGenericos(
        "Desconto maior que o saldo a receber",
      );
    }
    const chave = propriedade.endsWith("Porcentagem")
      ? `${propriedade.substring(0, propriedade.length - 11)}`
      : `${propriedade}Porcentagem`;
    const converter = {
      multa: converterValorParaPorcetagem,
      juros: converterValorParaPorcetagem,
      desconto: converterValorParaPorcetagem,
      multaPorcentagem: converterPorcentagemParaValor,
      jurosPorcentagem: converterPorcentagemParaValor,
      descontoPorcentagem: converterPorcentagemParaValor,
    };
    const pagamentoAtualizado = {
      ...pagamento,
      [propriedade]: valor,
      [chave]: converter[propriedade](valor, contaPagar.saldoPagar),
    };
    setPagamento({
      ...pagamentoAtualizado,
      valor: calcularValorAPagar(
        pagamentoAtualizado,
        dadosPagamentoList,
        contaPagar,
      ),
    });
  };

  const validarBaixarTitulo = async (event) => {
    event.preventDefault();
    const dadosPagamento = new Pagamento(
      pagamento,
      dadosPagamentoList,
      contaPagar,
    );
    const caixaAberto = await validarStatusCaixa(
      pagamento?.dataPagamento,
      dadosPagamento?.bancoCaixaId,
    );
    if (!caixaAberto) {
      const caixaSelecionado = contaList.find(
        (conta) => conta.id === dadosPagamento?.bancoCaixaId,
      );
      if (!caixaSelecionado?.aberturaAutomatica) {
        const retornoAlerta = await notification.confirmacao(
          "Abertura de Caixa",
          "Caixa se encontra fechado, confirma a abertura do caixa?",
        );
        if (retornoAlerta.isConfirmed) {
          await cadastrarCaixa(new Date(), dadosPagamento?.bancoCaixaId, true);
        }
      } else {
        await cadastrarCaixa(new Date(), dadosPagamento?.bancoCaixaId, true);
      }
    }
    setInputErro([]);
    pagamentoValidator
      .validate(dadosPagamento, { abortEarly: false })
      .then(() => {
        if (dadosPagamento.validaValor()) {
          cadastrarPagamentos(dadosPagamento);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const cadastrarPagamentos = async (body) => {
    const fornecedor = recebedorList.find(
      (item) => item.id == contaPagar.pessoaId,
    );
    if (
      Number(pagamento.valor) !== contaPagar.saldoPagar &&
      !pagamento.desconto
    ) {
      const retornoAlerta = await notification.confirmacao(
        "Baixa Parcial",
        "Confirma a baixa parcial do título?",
      );
      if (retornoAlerta.isConfirmed) {
        setLoading(true);
        const res = await contasPagarService.cadastrarPagamentos(id, body);
        setLoading(false);
        if (!res.isAxiosError) {
          setDadosPagamentoList((prevState) => [...prevState, res.data]);
          setReciboData({
            fornecedor,
            titulo: contaPagar,
            pagamentos: [res.data],
          });
          const retornoAlerta = await Swal.fire({
            title: "Baixa parcial feita com sucesso!",
            text: `Número do título: ${contaPagar?.numeroTitulo}`,
            icon: "success",
            confirmButtonText: "OK",
          });
          if (retornoAlerta.isConfirmed) {
            initialCollapses();
            voltar();
            handlePrint();
          }
        } else {
          setLoading(false);
          responseErros(res);
        }
      } else {
        setLoading(false);
      }
    } else {
      const retornoAlerta = await notification.confirmacao(
        "Baixar Título",
        "Confirma a baixa do título?",
      );
      if (retornoAlerta.isConfirmed) {
        setLoading(true);
        const res = await contasPagarService.cadastrarPagamentos(id, body);
        setLoading(false);
        if (!res.isAxiosError) {
          setDadosPagamentoList((prevState) => [...prevState, res.data]);
          setReciboData({
            fornecedor,
            titulo: contaPagar,
            pagamentos: [res.data],
          });
          const retornoAlerta = await Swal.fire({
            title: "Título quitado com sucesso!",
            text: `Número do título: ${contaPagar?.numeroTitulo}`,
            icon: "success",
            confirmButtonText: "OK",
          });
          if (retornoAlerta.isConfirmed) {
            initialCollapses();
            voltar();
            handlePrint();
          }
        } else {
          setLoading(false);
          responseErros(res);
        }
      }
    }
  };

  const handleOpenRetirarBaixa = () => {
    const podeSolicitarRetirada = permissoesHelper.temPermisao([
      "retirar-baixa",
      "pedir-senha-retirar-baixa",
    ]);
    if (!podeSolicitarRetirada) {
      return notification.alertaGenericos(
        "Você não tem permissão para retirar baixa",
      );
    }
    setOpenRetirarBaixaDialog(true);
  };

  const renderizarRetirarBaixaButton = () => {
    const baixasReais = filtrarPagamentosEfetivos(dadosPagamentoList);
    return contaPagar?.status !== "ABERTO" && !!baixasReais.length;
  };

  return (
    <>
      <form onSubmit={validarBaixarTitulo}>
        <Grid container spacing={2} className="mt-3">
          <Grid item xs={3}>
            <DatePicker
              id="dataPagamento"
              name="dataPagamento"
              label="Data de Pagamento"
              format="dd/MM/yyyy"
              maxDate={new Date()}
              required
              margin
              value={pagamento?.dataPagamento}
              error={inputErros.get("dataPagamento")}
              onChange={(date) => handleDateChange(date, "dataPagamento")}
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
            />
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="bancoCaixaId"
                name="bancoCaixaId"
                options={contaList}
                autoHighlight
                getOptionLabel={(option) => (option.nome ? option.nome : "")}
                getOptionSelected={(option, value) =>
                  option.nome === value.nome
                }
                noOptionsText="Sem opções"
                disabled={
                  contaPagar?.status === "BAIXADO" ||
                  contaPagar?.status === "RENEGOCIADO" ||
                  contaPagar?.status === "CANCELADO" ||
                  colaborador?.bancoCaixaId
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Banco/Caixa"
                    variant="outlined"
                    required
                    disabled={
                      contaPagar?.status === "BAIXADO" ||
                      contaPagar?.status === "RENEGOCIADO" ||
                      contaPagar?.status === "CANCELADO" ||
                      colaborador?.bancoCaixaId
                    }
                    error={inputErros.get("bancoCaixaId")}
                  />
                )}
                onChange={(_, newValue) => {
                  onChangeAutocomplete("bancoCaixaId", newValue);
                }}
                value={value.autoComplete(
                  contaList,
                  pagamento?.bancoCaixaId ?? contaPagar.bancoCaixaId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="meioPagamento-dadosPagamento"
                name="meioPagamento-dadosPagamento"
                options={meioPagamentoList}
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                noOptionsText="Sem opções"
                disabled={
                  contaPagar?.status === "BAIXADO" ||
                  contaPagar?.status === "RENEGOCIADO" ||
                  contaPagar?.status === "CANCELADO"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Meio de Pagamento"
                    variant="outlined"
                    required
                    disabled={
                      contaPagar?.status === "BAIXADO" ||
                      contaPagar?.status === "RENEGOCIADO" ||
                      contaPagar?.status === "CANCELADO"
                    }
                    error={inputErros.get("meioPagamentoId")}
                  />
                )}
                onChange={(_, newValue) =>
                  onChangeAutocomplete("meioPagamentoId", newValue)
                }
                value={value.autoComplete(
                  meioPagamentoList,
                  pagamento?.meioPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField
              id="atraso"
              name="atraso"
              label="Atraso (Dias)"
              variant="outlined"
              margin="normal"
              type="number"
              disabled
              value={getAtrasoDias(
                contaPagar?.dataVencimento,
                pagamento?.dataPagamento,
              )}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <TextFielDecimalNumbers
              id="multaPorcentagem"
              name="multaPorcentagem"
              label="Multa (%)"
              variant="outlined"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.multaPorcentagem || 0}
              onBlur={handleInputValorPorcentagem}
            />
          </Grid>
          <Grid item xs={2}>
            <CurrencyInput
              id="multa"
              name="multa"
              label="Multa (R$)"
              variant="outlined"
              margin="normal"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.multa || 0}
              onBlur={handleInputValorPorcentagem}
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <TextFielDecimalNumbers
              id="jurosPorcentagem"
              name="jurosPorcentagem"
              label="Juros (%)"
              variant="outlined"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.jurosPorcentagem || 0}
              onBlur={handleInputValorPorcentagem}
            />
          </Grid>
          <Grid item xs={2}>
            <CurrencyInput
              id="juros"
              name="juros"
              label="Juros (R$)"
              variant="outlined"
              margin="normal"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.juros || 0}
              onBlur={handleInputValorPorcentagem}
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <TextFielDecimalNumbers
              id="descontoPorcentagem"
              name="descontoPorcentagem"
              label="Desconto (%)"
              variant="outlined"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.descontoPorcentagem || 0}
              onBlur={handleInputValorPorcentagem}
            />
          </Grid>
          <Grid item xs={2}>
            <CurrencyInput
              id="desconto"
              name="desconto"
              label="Desconto (R$)"
              variant="outlined"
              margin="normal"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.desconto || 0}
              onBlur={handleInputValorPorcentagem}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className="d-flex justify-content-end">
          <Grid item xs={2}>
            <CurrencyInput
              id="valor"
              name="valor"
              label={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
                  ? "Valor Pago (R$)"
                  : "Valor a Pagar (R$)"
              }
              variant="outlined"
              margin="normal"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              value={pagamento?.valor || 0}
              onBlur={handleValorPagamentoChange}
              fullWidth
              error={inputErros.get("valor")}
            />
          </Grid>
          <Grid item xs={2} className="mt-3">
            <Button
              type="submit"
              size="large"
              variant="contained"
              color="success"
              disabled={
                contaPagar?.status === "BAIXADO" ||
                contaPagar?.status === "RENEGOCIADO" ||
                contaPagar?.status === "CANCELADO"
              }
              margin="normal"
              style={{ width: "100%", padding: "13px" }}
            >
              Baixar Título
            </Button>
          </Grid>
          {renderizarRetirarBaixaButton() && (
            <Grid item xs={2} className="mt-3">
              <Button
                size="large"
                variant="contained"
                color="padrao"
                margin="normal"
                onClick={handleOpenRetirarBaixa}
                style={{ width: "100%", padding: "13px" }}
              >
                Retirar Baixa
              </Button>
            </Grid>
          )}
        </Grid>
      </form>
      <RetirarBaixaDialog
        id={id}
        open={openRetirarBaixaDialog}
        setOpen={setOpenRetirarBaixaDialog}
        getAtrasoDias={getAtrasoDias}
        contaList={contaList}
        contaPagar={contaPagar}
        pagamento={pagamento}
        recebedorList={recebedorList}
        meioPagamentoList={meioPagamentoList}
        dadosPagamentoList={dadosPagamentoList}
        setDadosPagamentoList={setDadosPagamentoList}
        configuracoes={configuracoes}
      />
    </>
  );
};

export default DadosPagamento;
